import React, {useEffect, useState, useContext} from 'react';
import {Icon, Button, Dialogue, Modal, TextField, Text} from '@albert-io/atomic';
import {ContentDiscoveryContext} from '../../ContentDiscoveryProvider.react';

import './navigation-modals.scss';

export const warnUser = (e: BeforeUnloadEvent) => {
  e.returnValue = true;
  e.preventDefault();
};

const warningModalOptions = {
  body: 'Would you like to save an assignment draft before you leave?',
  Icon: <Icon icon={['fal', 'circle-exclamation']} />,
  Primary(props) {
    return <Button {...props}>Save a draft</Button>;
  },
  Secondary(props) {
    return (
      <Button variant='outlined' {...props}>
        Leave and don&apos;t save
      </Button>
    );
  }
};

const errorModalOptions = {
  body: 'We were unable to save changes to your assignment draft.',
  Icon: <Icon icon={['fal', 'triangle-exclamation']} color='danger' />,
  Primary(props) {
    return (
      <Button {...props} variant='outlined'>
        Stay On Page
      </Button>
    );
  },
  Secondary(props) {
    return (
      <Button color='destructive' variant='outlined' {...props}>
        Leave & Lose Changes
      </Button>
    );
  }
};

interface SaveDraftModalProps {
  confirmLeave: () => void;
  saveError: boolean;
  dismiss: () => void;
  handleSave: () => void;
  onMount: () => void;
}

export const SaveDraftModal = ({
  dismiss,
  handleSave,
  confirmLeave,
  saveError,
  onMount
}: SaveDraftModalProps) => {
  const PrimaryButton = saveError ? errorModalOptions.Primary : warningModalOptions.Primary;
  const SecondaryButton = saveError ? errorModalOptions.Secondary : warningModalOptions.Secondary;

  useEffect(() => {
    onMount();
  }, [onMount]);

  return (
    <Modal ariaLabel='You have unsaved draft changes' handleClose={dismiss}>
      {({modalContentStyle}) => {
        return (
          <Dialogue inModal className={modalContentStyle} hideCloseBtn>
            <Dialogue.Title align='center'>
              {saveError ? errorModalOptions.Icon : warningModalOptions.Icon}
            </Dialogue.Title>
            <Dialogue.Body>
              {saveError ? errorModalOptions.body : warningModalOptions.body}
            </Dialogue.Body>
            <Dialogue.BtnGroup direction='column' fillColumn>
              <PrimaryButton onClick={handleSave} />
              <SecondaryButton
                onClick={() => {
                  window.removeEventListener('beforeunload', warnUser);
                  confirmLeave();
                }}
              />
            </Dialogue.BtnGroup>
          </Dialogue>
        );
      }}
    </Modal>
  );
};

interface InputModalProps {
  onSave: (draftName: string) => void;
  onDismiss: () => void;
}

export const SaveWithNameInputModal = ({onSave, onDismiss}: InputModalProps) => {
  const [draftName, setDraftName] = useState('');
  return (
    <Modal ariaLabel='Save a draft' dismissable={false}>
      {({modalContentStyle}) => {
        return (
          <Dialogue hideCloseBtn inModal className={modalContentStyle}>
            <Dialogue.Title align='center'>
              <Icon color='midnight-lightest' icon={['far', 'file-alt']} />
            </Dialogue.Title>
            <Dialogue.Body>
              <Text className='u-display_flex u-mar-b_2 u-justify-content_center u-width_100pc'>
                Name your assignment draft:
              </Text>
              <TextField
                size='l'
                value={draftName}
                onChange={(e) => {
                  setDraftName(e.target.value);
                }}
              />
            </Dialogue.Body>
            <Dialogue.BtnGroup align='center' fillColumn>
              <Button color='secondary' variant='outlined' onClick={onDismiss}>
                Don&apos;t Save
              </Button>
              <Button
                variant='outlined'
                onClick={() => {
                  onSave(draftName);
                }}
                disabled={draftName.length === 0}
              >
                Save Draft
              </Button>
            </Dialogue.BtnGroup>
          </Dialogue>
        );
      }}
    </Modal>
  );
};

interface DiscardModalProps {
  confirmLeave: () => void;
  dismiss: () => void;
  openFolderModal: () => void;
}

export const DiscardModal = ({dismiss, confirmLeave, openFolderModal}: DiscardModalProps) => {
  const {selectedQuestionSets} = useContext(ContentDiscoveryContext);
  return (
    <Modal ariaLabel='You have unsaved draft changes' handleClose={dismiss}>
      {({modalContentStyle}) => {
        return (
          <Dialogue paddingLevel={0} inModal className={modalContentStyle} hideCloseBtn>
            <Icon
              icon={['fal', 'hand']}
              className='fa-2x u-mar-b_4 u-mar-t_4 u-mar-lr_auto u-display_block'
            />
            <Dialogue.Body className='u-pad-lr_4'>
              <Text className='u-display_flex u-justify-content_center u-width_100pc'>
                Are you sure you want to leave and discard your work?
              </Text>
            </Dialogue.Body>
            <Dialogue.BtnGroup align='center' className='u-pad-lr_4 u-pad-b_4'>
              <Button onClick={() => dismiss()} variant='outlined'>
                Keep Editing
              </Button>
              <Button
                onClick={() => {
                  window.removeEventListener('beforeunload', warnUser);
                  confirmLeave();
                }}
              >
                Leave & Discard
              </Button>
            </Dialogue.BtnGroup>
            {selectedQuestionSets.size > 0 && (
              <Dialogue.Body className='discard-modal__folder-div u-bgc_slate-100 u-pad-lr_4 u-pad-tb_2 u-text-align_center'>
                <Text as='p' italic>
                  Trying to save your progress?
                </Text>
                <Text as='p' italic>
                  <Button
                    variant='text'
                    onClick={() => {
                      openFolderModal();
                      dismiss();
                    }}
                    size='s'
                    className='u-text-decoration_underline u-font-style_italic'
                  >
                    Convert this assignment to a folder
                  </Button>
                  to move these questions to your Folders.
                </Text>
                <Text as='p' className='u-mar-b_0' italic>
                  (Coming Soon: Ability to save assignments!)
                </Text>
              </Dialogue.Body>
            )}
          </Dialogue>
        );
      }}
    </Modal>
  );
};
